/**
 * Tutor (TASK-014 Step B2) — the full-page tutor: conversations list (left,
 * ~280px) + wide chat. Distinct from `TutorDrawer` (the contextual "chat sul
 * blocco" panel inside Lettura.tsx, unaffected by this task — see task doc
 * "Tutor centrale" decision).
 *
 * Navigation is URL-driven, never client state (task doc "niente stato
 * client parallelo"): picking a conversation or starting a new one is a real
 * Inertia `<Link>`/`router.visit` to `/read/:id/tutor(/:conversationId)` —
 * the server (ReaderController#tutor) decides which conversation is
 * "active" from the URL; this page never keeps its own selection state.
 * `TutorConversationPane` is `key`ed by the active conversation's id so
 * switching conversations always mounts a FRESH `useTutorConversation`
 * instance — that hook's own contract is that `initialConversationId`/
 * `initialMessages` are read once, at construction (see
 * useTutorConversation.ts), so re-rendering the SAME instance with new props
 * would silently keep showing the old conversation.
 *
 * The conversations list + the active conversation's summary/messages both
 * come from the `tutor` Inertia prop (server-authoritative, built by
 * ReaderController#build_tutor_prop / Tutor::ConversationSerializer) — after
 * every turn (`onTurnSettled`) this page does a partial Inertia reload
 * (`router.reload({ only: ['tutor'] })`, same pattern as Lettura.tsx's TOC
 * refresh) instead of keeping a second, client-fetched copy of the list.
 *
 * Mobile (<768px): the list IS the default view; an active conversation
 * takes over the full width with a "‹ Conversazioni" back link. Desktop:
 * both columns are always visible side by side. Pure Tailwind `md:`
 * toggling, same pattern as CourseTabs — no JS breakpoint logic.
 *
 * "Primo messaggio da Panoramica" handoff (task doc Step B2): when the URL
 * carries `?draft=<text>` (see urls.ts `tutorConversationUrl`), the active
 * conversation's pane sends that text as its first message on mount, then
 * scrubs the param so a refresh never re-sends it — see
 * `TutorConversationPane`.
 *
 * Citation/action deep-link into the manual: a citation's "Apri nel
 * manuale" button and an assistant's `open_section` action both need a
 * *slug* for a `section_id`. This page has NO full toc ("ogni pagina
 * riceve solo i dati della propria vista" — task doc invariant covers bulk
 * CONTENT, not a tiny id index), unlike TutorDrawer (lives INSIDE
 * Lettura.tsx and reuses that page's already-loaded toc). Instead
 * `resolveSectionTarget` resolves through `tutor.sectionSlugs` — the
 * lightweight section_id -> slug map ReaderController#build_tutor_prop
 * sends alongside the conversation list — via tutorNav.ts's
 * `resolveTutorSectionTargetFromSlugs` (same resolution contract as
 * Lettura.tsx's `resolveTutorSectionTarget`: exact fine id match, else
 * longest dotted-prefix match, else the bare section_id).
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, router } from '@inertiajs/react'
import Heading from '@arianna/design-system/Heading'
import Text from '@arianna/design-system/Text'
import { createTutorConversation } from './tutorApi.js'
import { sectionUrl, quizSessionUrl, tutorConversationUrl, tutorUrl } from './urls.js'
import { resolveTutorSectionTargetFromSlugs } from './tutorNav.js'
import CourseLayout from './CourseLayout.js'
import type { PageComponent } from './inertiaPage.js'
import type { TutorConversationListItemProp, TutorPageProps } from './types.js'
import { useTutorConversation } from './tutor/useTutorConversation.js'
import { TutorConversationView } from './tutor/TutorConversationView.js'
import type { TutorViewMessage } from './tutor/TutorMessageBubble.js'
import { formatConversationTimestamp, toViewMessages } from './tutor/tutorViewModel.js'

// ---------------------------------------------------------------------------
// Sidebar — conversations list.
// ---------------------------------------------------------------------------

function ConversationListItem({
  conversation,
  active,
  manualVersionId,
}: {
  conversation: TutorConversationListItemProp
  active: boolean
  manualVersionId: number
}) {
  return (
    <Link
      href={tutorConversationUrl(manualVersionId, conversation.id)}
      aria-current={active ? 'page' : undefined}
      className={`flex flex-col gap-0.5 px-3 py-2.5 rounded-md no-underline border-l-2 transition-colors ${
        active
          ? 'border-accent bg-accent-bg text-ink'
          : 'border-transparent text-ink-2 hover:bg-surface-2'
      }`}
    >
      <Text variant="ui" className="truncate">
        {conversation.title ?? 'Nuova conversazione'}
      </Text>
      <div className="flex items-center gap-2">
        {conversation.lastMessageAt != null && (
          <Text variant="meta" className="text-ink-4">
            {formatConversationTimestamp(conversation.lastMessageAt)}
          </Text>
        )}
        <Text variant="meta" className="text-ink-4">
          {conversation.messageCount} {conversation.messageCount === 1 ? 'messaggio' : 'messaggi'}
        </Text>
      </div>
    </Link>
  )
}

// ---------------------------------------------------------------------------
// Active conversation pane — owns the real `useTutorConversation` instance.
// ---------------------------------------------------------------------------

function scrubDraftParam(): void {
  const url = new URL(window.location.href)
  if (!url.searchParams.has('draft')) return
  url.searchParams.delete('draft')
  window.history.replaceState(window.history.state, '', url.toString())
}

interface TutorConversationPaneProps {
  manualVersionId: number
  conversationId: string
  initialMessages: TutorViewMessage[]
  sectionSlugs: Record<string, string>
  onTurnSettled: () => void
}

function TutorConversationPane({
  manualVersionId,
  conversationId,
  initialMessages,
  sectionSlugs,
  onTurnSettled,
}: TutorConversationPaneProps) {
  const handleNavigateToSection = useCallback(
    (slug: string, anchor: string) => {
      router.visit(`${sectionUrl(manualVersionId, slug)}${anchor}`)
    },
    [manualVersionId],
  )

  const handleLaunchQuiz = useCallback(
    (quizSessionId: string) => {
      router.visit(quizSessionUrl(manualVersionId, quizSessionId))
    },
    [manualVersionId],
  )

  // See this file's top doc — no full toc on this page, so citation/action
  // deep-links resolve through the lightweight `sectionSlugs` id->slug map
  // instead (same resolution contract as Lettura.tsx's
  // resolveTutorSectionTarget).
  const resolveSectionTarget = useCallback(
    (sectionId: string, fineId?: string | null) =>
      resolveTutorSectionTargetFromSlugs(sectionSlugs, sectionId, fineId),
    [sectionSlugs],
  )

  const conversation = useTutorConversation({
    manualVersionId,
    initialConversationId: conversationId,
    initialMessages,
    resolveSectionTarget,
    onNavigateToSection: handleNavigateToSection,
    onLaunchQuiz: handleLaunchQuiz,
    onTurnSettled,
  })

  // "Primo messaggio da Panoramica" (task doc Step B2) — `draftParam` is
  // read ONCE at mount (this component is remounted per conversation via the
  // parent's `key`, so "at mount" == "when this conversation became
  // active"). Guarded twice against a double-send: the URL is scrubbed
  // immediately once the send is kicked off (a refresh after that sees no
  // `?draft=` at all), AND `autoSendDoneRef` blocks this effect from ever
  // running its send branch twice within the SAME pane instance.
  const [draftParam] = useState(() => new URLSearchParams(window.location.search).get('draft'))
  const autoSendDoneRef = useRef(false)

  useEffect(() => {
    if (draftParam == null || autoSendDoneRef.current) return

    // Guard (task doc): never auto-send into a conversation that already has
    // a user message — covers a stale/replayed `?draft=` on an existing
    // conversation.
    const hasUserMessage = conversation.messages.some((m) => m.role === 'user')
    if (hasUserMessage) {
      autoSendDoneRef.current = true
      scrubDraftParam()
      return
    }

    if (conversation.draft !== draftParam) {
      conversation.setDraft(draftParam)
      return
    }

    autoSendDoneRef.current = true
    scrubDraftParam()
    void conversation.handleSend()
    // conversation.messages/.draft/.setDraft/.handleSend are the exact
    // reactive inputs this effect branches on — see the ref-guarded early
    // returns above for why re-running it (e.g. while messages stream in) is
    // always a cheap no-op once armed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftParam, conversation.draft, conversation.messages])

  const isEmpty = !conversation.messagesLoading && conversation.messages.length === 0

  return (
    <TutorConversationView
      emptyStateMessage="Chiedimi quello che vuoi su questo manuale — anche i dubbi che ti sembrano scontati."
      isEmpty={isEmpty}
      {...conversation}
    />
  )
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const Tutor: PageComponent<TutorPageProps> = ({ shell, tutor }) => {
  const manualVersionId = shell.manualVersionId
  const { conversations, active, sectionSlugs } = tutor
  const hasActiveConversation = active != null

  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)

  const handleNewConversation = useCallback(() => {
    if (creating) return
    setCreating(true)
    setCreateError(null)
    createTutorConversation(manualVersionId)
      .then((created) => {
        router.visit(tutorConversationUrl(manualVersionId, created.id))
      })
      .catch((err: unknown) => {
        setCreating(false)
        setCreateError(err instanceof Error ? err.message : String(err))
      })
  }, [creating, manualVersionId])

  // Refetches `tutor` (list + active) after every turn — server stays the
  // single source of truth for title/last_message_at/messageCount, no
  // parallel client-fetched list (see this file's top doc).
  const handleTurnSettled = useCallback(() => {
    router.reload({ only: ['tutor'] })
  }, [])

  return (
    <div className="flex-1 min-h-0 flex overflow-hidden">
      {/* Sidebar — full-width default view on mobile (<768px); hidden there
          once a conversation is active. Always visible on desktop. */}
      <aside
        className={`w-full md:w-[280px] md:shrink-0 md:flex flex-col border-r border-line bg-surface overflow-y-auto ${
          hasActiveConversation ? 'hidden md:flex' : 'flex'
        }`}
      >
        <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-line shrink-0">
          <Heading variant="h3" style={{ color: 'var(--ink)' }}>
            Tutor
          </Heading>
          <button
            onClick={handleNewConversation}
            disabled={creating}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium text-accent bg-accent-bg border border-accent cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
          >
            + Nuova
          </button>
        </div>

        {createError != null && (
          <div className="px-4 py-2 shrink-0">
            <Text variant="meta" className="text-red">
              {createError}
            </Text>
          </div>
        )}

        {conversations.length === 0 ? (
          <div className="flex-1 flex items-center justify-center px-6 py-8">
            <Text variant="meta" className="text-ink-3 text-center">
              Non hai ancora nessuna conversazione. Tocca «+ Nuova» e chiedi al tutor quello che
              vuoi su questo manuale.
            </Text>
          </div>
        ) : (
          <div className="flex flex-col gap-1 p-2">
            {conversations.map((c) => (
              <ConversationListItem
                key={c.id}
                conversation={c}
                active={active?.id === c.id}
                manualVersionId={manualVersionId}
              />
            ))}
          </div>
        )}
      </aside>

      {/* Chat column — full-width on mobile once a conversation is active,
          always visible on desktop (empty-state placeholder when none is
          selected). Content itself is capped to a ~760px centered column
          (task mockup), narrower than TutorConversationView's own internal
          max-w-5xl, which just never binds inside this narrower parent. */}
      <div
        className={`flex-1 min-h-0 flex-col overflow-hidden ${
          hasActiveConversation ? 'flex' : 'hidden md:flex'
        }`}
      >
        {active != null ? (
          <>
            <div className="md:hidden shrink-0 border-b border-line px-4 py-2.5">
              <Link
                href={tutorUrl(manualVersionId)}
                className="inline-flex items-center gap-1 text-sm text-ink-2 no-underline"
              >
                ‹ Conversazioni
              </Link>
            </div>
            <div className="flex-1 min-h-0 flex flex-col w-full max-w-[760px] mx-auto">
              <TutorConversationPane
                key={active.id}
                manualVersionId={manualVersionId}
                conversationId={active.id}
                initialMessages={toViewMessages(active.messages)}
                sectionSlugs={sectionSlugs}
                onTurnSettled={handleTurnSettled}
              />
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center px-6">
            <Text variant="lead" className="text-ink-3 text-center max-w-sm">
              Seleziona una conversazione qui a fianco, o iniziane una nuova per parlare col tutor.
            </Text>
          </div>
        )}
      </div>
    </div>
  )
}

Tutor.layout = (page) => <CourseLayout>{page}</CourseLayout>

export default Tutor
